import { Component, Inject, OnInit } from '@angular/core';
import { StateService, Transition } from '@uirouter/angular';
import { Observable } from 'rxjs';
import { animate, style, transition, trigger } from '@angular/animations';
import { NotificationsDataService } from '../../data-access/notifications-data.service';
import { LegacyNotificationActionsService } from '../../data-access/legacy-notification-actions.service';
import {
  ProjectSummaryAction,
  ProjectSummaryActionContext,
  ProjectSummaryRow,
  ProjectSummarySort
} from '../../../project-summary/data-access/project-summary.types';
import { NotificationSort, NotificationWatch } from '../../data-access/notifications.types';
import { FEATURES_TOKEN } from '../../../config/config.tokens';
import { Features } from '../../../config/config.model';
import { CurrentUserService } from '../../../shared/services/current-user.service';
import { ProjectDataService } from '../../../project-display/data-access/project-data.service';
import { IProject } from '../../../project-display/interfaces/IProject';

@Component({
  selector: 'app-project-watch-page',
  templateUrl: './project-watch-page.component.html',
  styleUrls: ['./project-watch-page.component.scss'],
  animations: [
    trigger('fadeSlide', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('320ms ease-out', style({ transform: 'translateX(0)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('180ms ease-in', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class ProjectWatchPageComponent implements OnInit {
  private readonly summarySortStorageKey = 'notifications.projectSummary.sort';
  watchGuid = '';
  watchName = '';
  watchNameParam = '';
  flyoutProjectId: number | null = null;
  flyoutProject$?: Observable<IProject>;
  watch?: NotificationWatch;
  projects: unknown[] = [];
  selectedProjectIds: number[] = [];
  newSince?: string;
  highlightSinceDate?: string;
  sort: NotificationSort = { sortField: 'ProjectUpdated', sortDesc: true };
  exportEnabled = true;
  exportSuspended = false;
  shareDisabled = false;
  selectedProjectsPdfEnabled = false;
  updatedProjectsPdfEnabled = false;
  mapEnabled = false;
  isEicStaff = false;
  summaryActions: ProjectSummaryAction[] = [
    {
      id: 'exportSummary',
      label: 'Export Summary...',
      role: 'secondary',
      visible: () => this.exportEnabled && !this.exportSuspended,
      onClick: (context) => this.exportSummary(context)
    },
    {
      id: 'exportSuspended',
      label: 'Export Service Suspended',
      role: 'default',
      visible: () => this.exportSuspended,
      disabled: () => true,
      onClick: () => { }
    },
    {
      id: 'share',
      label: 'Share...',
      role: 'default',
      disabled: (context) => this.shareDisabled || context.selectedCount > 0,
      onClick: () => this.share()
    },
    {
      id: 'pdfSelected',
      label: 'PDF of selected projects...',
      role: 'default',
      visible: () => this.selectedProjectsPdfEnabled,
      disabled: (context) => context.selectedCount === 0,
      onClick: (context) => this.requestPdfSelectedProjects(context)
    },
    {
      id: 'pdfUpdated',
      label: 'PDF of projects updated since last notification...',
      role: 'default',
      visible: () => this.updatedProjectsPdfEnabled,
      disabled: (context) => context.selectedCount === 0,
      onClick: (context) => this.requestPdfUpdatedProjects(context)
    },
    {
      id: 'stopWatching',
      label: 'Stop watching selected projects',
      role: 'danger',
      disabled: (context) => context.selectedCount === 0 || context.allSelectableSelected,
      onClick: () => this.stopWatchingSelectedProjects()
    },
    {
      id: 'map',
      label: 'Show selected projects on a map... (Beta)',
      role: 'default',
      visible: () => this.mapEnabled && this.isEicStaff,
      disabled: (context) => context.selectedCount === 0,
      onClick: (context) => this.showSelectedProjectsOnMap(context)
    }
  ];

  constructor(
    private transition: Transition,
    private notificationsData: NotificationsDataService,
    private legacyActions: LegacyNotificationActionsService,
    private stateService: StateService,
    private projectDataService: ProjectDataService,
    private currentUser: CurrentUserService,
    @Inject(FEATURES_TOKEN) private features: Features
  ) { }

  ngOnInit(): void {
    const guid = this.transition.params().guid as string;
    this.watchGuid = guid;
    this.watchNameParam = (this.transition.params().name as string) || '';
    this.watchName = decodeURIComponent(this.watchNameParam || '');
    this.applyFlyoutFromParams(this.transition.params().projectID, this.transition.params().projectName);

    const switches = this.features.legacySwitches;
    const projectSummaryConfig = this.features.projectSummary;
    const projectExports = projectSummaryConfig?.projectExports;
    const pdfReports = projectSummaryConfig?.pdfReports;

    this.exportEnabled = !!(projectExports?.projectExportEnabled ?? switches?.NEW_EXPORTS?.PROJECT_EXPORT_ENABLED);
    this.exportSuspended = !!(projectExports?.suspended ?? switches?.NEW_EXPORTS?.SUSPENDED);
    this.shareDisabled = !!switches?.WATCHES?.SHARE_DISABLED;
    this.selectedProjectsPdfEnabled = !!(pdfReports?.selectedProjectsEnabled ?? switches?.WATCHES?.SELECTED_PROJECTS_PDF_ENABLED);
    this.updatedProjectsPdfEnabled = !!(pdfReports?.criteriaProjectsEnabled ?? switches?.WATCHES?.UPDATED_PROJECTS_PDF_ENABLED);
    this.mapEnabled = !!switches?.GEOGRAPHICAL_PROJECT_PRESENTER?.enabled;

    const userRoles = this.currentUser.getSnapshot()?.roles || [];
    this.isEicStaff = userRoles.some((role) => (role || '').toLowerCase() === 'eicstaff');

    if (!guid) {
      return;
    }

    this.sort = this.loadStoredSort(this.summarySortStorageKey, this.sort);

    this.loadWatchDetail();
  }

  loadWatchDetail(): void {
    this.notificationsData.getWatchDetail(this.watchGuid, this.sort).subscribe((watchDetail) => {
      this.watch = watchDetail?.watch;
      this.projects = watchDetail?.projects || [];
      this.highlightSinceDate = watchDetail?.highlightSinceDate;
      this.newSince = watchDetail?.newSince;

      if (!this.watchName) {
        this.watchName = watchDetail?.watch?.name || '';
      }
    });
  }

  onSortChanged(sort: ProjectSummarySort): void {
    this.sort = { sortField: sort.sortField, sortDesc: sort.sortDesc };
    this.storeSort(this.summarySortStorageKey, this.sort);
    this.loadWatchDetail();
  }

  onSelectionChanged(projectIds: number[]): void {
    this.selectedProjectIds = projectIds;
  }

  get isProjectFlyoutOpen(): boolean {
    return this.flyoutProjectId !== null;
  }

  openProject(row: ProjectSummaryRow): void {
    this.setFlyoutProject(row.projectID, row.projectName || '');
    this.stateService.go('projectWatch', {
      guid: this.watchGuid,
      name: this.watchNameParam || encodeURIComponent(this.watchName || ''),
      projectID: row.projectID,
      projectName: row.projectName || ''
    }, { notify: false, location: 'replace', inherit: true, reload: false });
  }

  closeProjectFlyout(): void {
    if (!this.isProjectFlyoutOpen) {
      return;
    }

    this.setFlyoutProject(null);
    this.stateService.go('projectWatch', {
      guid: this.watchGuid,
      name: this.watchNameParam || encodeURIComponent(this.watchName || ''),
      projectID: null,
      projectName: null
    }, { notify: false, location: 'replace', inherit: true, reload: false });
  }

  onFlyoutClosed(): void {
    this.closeProjectFlyout();
  }

  stopWatchingSelectedProjects(): void {
    if (!this.selectedProjectIds.length) {
      return;
    }

    this.notificationsData.stopWatchingProjects(this.watchGuid, this.selectedProjectIds).subscribe(() => {
      const selected = new Set(this.selectedProjectIds);
      this.projects = (this.projects || []).map((item: any) =>
        selected.has(item.projectID) ? { ...item, excluded: true } : item
      );
      this.selectedProjectIds = [];
    });
  }

  requestPdfSelectedProjects(context?: ProjectSummaryActionContext): void {
    const projectIds = context?.selectedProjectIds || this.selectedProjectIds;
    this.legacyActions.openReportRequestDialogSelectedProjects(this.projects as ProjectSummaryRow[], projectIds);
  }

  requestPdfUpdatedProjects(context?: ProjectSummaryActionContext): void {
    const projectIds = context?.selectedProjectIds || this.selectedProjectIds;
    this.legacyActions.openReportRequestDialogSelectedProjects(this.projects as ProjectSummaryRow[], projectIds);
  }

  showSelectedProjectsOnMap(context?: ProjectSummaryActionContext): void {
    const projectIds = context?.selectedProjectIds || this.selectedProjectIds;
    this.legacyActions.openSelectedProjectsOnMap(this.projects as ProjectSummaryRow[], projectIds);
  }

  restoreProject(projectID: number): void {
    this.notificationsData.restoreProject(this.watchGuid, projectID).subscribe(() => {
      this.projects = (this.projects || []).map((item: any) =>
        item.projectID === projectID ? { ...item, excluded: false } : item
      );
    });
  }

  exportSummary(context?: ProjectSummaryActionContext): void {
    if (!this.exportEnabled || this.exportSuspended) {
      return;
    }

    const projectIds = context?.selectedProjectIds || this.selectedProjectIds;
    this.legacyActions.openProjectExportRequestDialog(this.projects as ProjectSummaryRow[], projectIds);
  }

  share(): void {
    if (this.shareDisabled || this.selectedProjectIds.length > 0) {
      return;
    }

    const currentPath = window.location.href.replace('#', '%23');
    const href = `mailto:?subject=Search Result from EICDataStream&body=I thought you might be interested in this list of projects from EICDataStream:%0D%0A%0D%0A${currentPath}%0D%0A%0D%0A`;
    window.location.href = href;
  }

  onEnableChanged(): void {
    if (!this.watch) {
      return;
    }

    this.notificationsData.enableWatch(this.watch).subscribe(() => {
      if (this.watch) {
        this.watch.nextNotification = this.notificationsData.calculateNextNotification(this.watch) || undefined;
      }
    });
  }

  private storeSort(key: string, sort: NotificationSort): void {
    try {
      sessionStorage.setItem(key, JSON.stringify(sort));
    } catch {
    }
  }

  private loadStoredSort(key: string, fallback: NotificationSort): NotificationSort {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) {
        return fallback;
      }

      const parsed = JSON.parse(raw) as Partial<NotificationSort>;
      if (typeof parsed.sortField !== 'string' || typeof parsed.sortDesc !== 'boolean') {
        return fallback;
      }

      return {
        sortField: parsed.sortField,
        sortDesc: parsed.sortDesc
      };
    } catch {
      return fallback;
    }
  }

  private applyFlyoutFromParams(projectIdParam: unknown, projectNameParam: unknown): void {
    const projectId = Number(projectIdParam);
    if (!Number.isFinite(projectId) || projectId <= 0) {
      this.setFlyoutProject(null);
      return;
    }

    const projectName = typeof projectNameParam === 'string' ? decodeURIComponent(projectNameParam) : '';
    this.setFlyoutProject(projectId, projectName);
  }

  private setFlyoutProject(projectId: number | null, projectName?: string): void {
    if (!projectId || projectId <= 0) {
      this.flyoutProjectId = null;
      this.flyoutProject$ = undefined;
      return;
    }

    this.flyoutProjectId = projectId;
    this.flyoutProject$ = this.projectDataService.getProject(projectId);
  }

}
